import { Flex, Text, Tooltip } from '@chakra-ui/react';
import { faEdit, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonIcon,
  ButtonText,
  FormFeedback,
  Input,
  Modal,
  useToast
} from '@morressier/alembic';
import { ISubmissionContext } from '@publishing-engine/domain/models';
import { ReactNode } from 'react';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext
} from 'react-hook-form';
import { useUpdateJournalSettings } from 'src/api/mutations';
import { FieldLabel } from 'src/components/Field';
import {
  buildJournalEmailsPayload,
  DUPLICATE_JOURNAL_EMAIL_MESSAGE,
  EMPTY_JOURNAL_EMAIL_ROW,
  getJournalEmailsFormValues,
  hasDuplicateJournalEmail,
  JournalEmailsFormValues,
  MAX_REPLY_TO_ROWS,
  validateJournalEmailAddress,
  validateJournalEmailName
} from './journalEmailsConfig';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  journal: ISubmissionContext;
}

/**
 * A name and an email side by side, with the row's own action at the end. Each
 * half carries its own message, because a row can hold two faults at once — an
 * address that is only whitespace is also a row whose name is still missing —
 * and a single message under the row would hide the one the user just caused.
 * The inputs arrive registered by the caller, which is what keeps each row's
 * path checked against the form values.
 */
const AddressRow = ({
  nameInput,
  emailInput,
  action,
  nameError,
  emailError
}: {
  nameInput: ReactNode;
  emailInput: ReactNode;
  action?: ReactNode;
  nameError?: string;
  emailError?: string;
}) => (
  <Flex gap={2} alignItems="flex-start" width="100%">
    <Flex flexDirection="column" gap={1} flex="1">
      {nameInput}
      {nameError ? (
        <FormFeedback variant="error">{nameError}</FormFeedback>
      ) : null}
    </Flex>
    <Flex flexDirection="column" gap={1} flex="1">
      {emailInput}
      {emailError ? (
        <FormFeedback variant="error">{emailError}</FormFeedback>
      ) : null}
    </Flex>
    {action}
  </Flex>
);

const RemoveRowButton = ({
  label,
  reason,
  onClick
}: {
  label: string;
  reason?: string;
  onClick: () => void;
}) => (
  <Tooltip
    hasArrow
    placement="top"
    label={reason ?? label}
    shouldWrapChildren
    isDisabled={!reason}
  >
    <ButtonIcon
      variant="caution"
      ariaLabel={label}
      icon={<FontAwesomeIcon icon={faTrash} style={{ color: 'red' }} />}
      onClick={onClick}
      isDisabled={Boolean(reason)}
    />
  </Tooltip>
);

const ReplyToSection = () => {
  const {
    register,
    formState: { errors }
  } = useFormContext<JournalEmailsFormValues>();
  const { fields, append } = useFieldArray<JournalEmailsFormValues, 'replyTo'>({
    name: 'replyTo'
  });
  const isFull = fields.length >= MAX_REPLY_TO_ROWS;

  return (
    <Flex flexDirection="column" gap={2}>
      <FieldLabel
        label="Journal support"
        subText=""
        helpText="The contact address displayed to users for journal-related help and support. This email address and its associated name are also used as the reply-to details for outgoing journal emails."
        isRequired={true}
      />
      {fields.map((field, index) => (
        <AddressRow
          key={field.id}
          nameInput={
            <Input
              placeholder="Name"
              {...register(`replyTo.${index}.name`, {
                validate: (value, values) =>
                  validateJournalEmailName(
                    value ?? '',
                    values.replyTo[index],
                    'Journal Support name',
                    true
                  )
              })}
              isInvalid={Boolean(errors.replyTo?.[index]?.name)}
            />
          }
          emailInput={
            <Input
              placeholder="Email"
              {...register(`replyTo.${index}.email`, {
                validate: (value, values) => {
                  const email = value?.trim() ?? '';
                  const addressValidation = validateJournalEmailAddress(
                    value ?? '',
                    values.replyTo[index],
                    'Journal Support email',
                    true
                  );
                  if (addressValidation !== true) {
                    return addressValidation;
                  }

                  return hasDuplicateJournalEmail(
                    values,
                    `replyTo.${index}.email`,
                    email
                  )
                    ? DUPLICATE_JOURNAL_EMAIL_MESSAGE
                    : true;
                }
              })}
              isInvalid={Boolean(errors.replyTo?.[index]?.email)}
            />
          }
          // action={
          //   <RemoveRowButton
          //     label="Remove reply-to"
          //     onClick={() => remove(index)}
          //   />
          // }
          nameError={errors.replyTo?.[index]?.name?.message}
          emailError={errors.replyTo?.[index]?.email?.message}
        />
      ))}
      <Flex justifyContent="flex-end" display="none">
        <Tooltip
          hasArrow
          placement="top"
          label="Only one reply-to address is supported for now."
          shouldWrapChildren
          isDisabled={!isFull}
        >
          <ButtonText
            iconLeft={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => append({ ...EMPTY_JOURNAL_EMAIL_ROW })}
            isDisabled={isFull}
          >
            Add Row
          </ButtonText>
        </Tooltip>
      </Flex>
    </Flex>
  );
};

const AdditionalEmailsSection = () => {
  const {
    register,
    formState: { errors }
  } = useFormContext<JournalEmailsFormValues>();
  const { fields, append, remove } = useFieldArray<
    JournalEmailsFormValues,
    'additionalEmails'
  >({ name: 'additionalEmails' });

  return (
    <Flex flexDirection="column" gap={2}>
      <FieldLabel
        label="Additional emails"
        subText=""
        helpText="Additional journal email addresses stored for reference or future use."
        isRequired={false}
      />
      {fields.map((field, index) => (
        <AddressRow
          key={field.id}
          nameInput={
            <Input
              placeholder="Name"
              {...register(`additionalEmails.${index}.name`, {
                validate: (value, values) =>
                  validateJournalEmailName(
                    value ?? '',
                    values.additionalEmails[index],
                    'Additional email name',
                    false
                  )
              })}
              isInvalid={Boolean(errors.additionalEmails?.[index]?.name)}
            />
          }
          emailInput={
            <Input
              placeholder="Email"
              {...register(`additionalEmails.${index}.email`, {
                validate: (value, values) => {
                  const address = value?.trim() ?? '';
                  const addressValidation = validateJournalEmailAddress(
                    value ?? '',
                    values.additionalEmails[index],
                    'Additional email',
                    false
                  );
                  if (addressValidation !== true) {
                    return addressValidation;
                  }

                  return hasDuplicateJournalEmail(
                    values,
                    `additionalEmails.${index}.email`,
                    address
                  )
                    ? DUPLICATE_JOURNAL_EMAIL_MESSAGE
                    : true;
                }
              })}
              isInvalid={Boolean(errors.additionalEmails?.[index]?.email)}
            />
          }
          action={
            <Flex alignItems="center" gap={2}>
              <RemoveRowButton
                label="Remove additional email"
                onClick={() => remove(index)}
              />
            </Flex>
          }
          nameError={errors.additionalEmails?.[index]?.name?.message}
          emailError={errors.additionalEmails?.[index]?.email?.message}
        />
      ))}
      <Flex justifyContent="flex-end">
        <ButtonText
          iconLeft={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => append({ ...EMPTY_JOURNAL_EMAIL_ROW })}
        >
          Add Row
        </ButtonText>
      </Flex>
    </Flex>
  );
};

const FromEmailSection = () => {
  const {
    register,
    formState: { errors }
  } = useFormContext<JournalEmailsFormValues>();

  return (
    <Flex flexDirection="column" gap={2}>
      <FieldLabel
        label="Journal from address"
        subText=""
        helpText="The email address used as the sender for automated emails sent by the journal."
        isRequired={false}
      />
      <AddressRow
        nameInput={
          <Input
            placeholder="Name"
            {...register('fromEmail.name', {
              validate: (value, values) =>
                validateJournalEmailName(
                  value ?? '',
                  values.fromEmail,
                  'From name',
                  false
                )
            })}
            isInvalid={Boolean(errors.fromEmail?.name)}
          />
        }
        emailInput={
          <Input
            placeholder="Email"
            {...register('fromEmail.email', {
              validate: (value, values) => {
                const addressValidation = validateJournalEmailAddress(
                  value ?? '',
                  values.fromEmail,
                  'From email',
                  false
                );
                if (addressValidation !== true) {
                  return addressValidation;
                }

                return hasDuplicateJournalEmail(
                  values,
                  'fromEmail.email',
                  value ?? ''
                )
                  ? DUPLICATE_JOURNAL_EMAIL_MESSAGE
                  : true;
              }
            })}
            isInvalid={Boolean(errors.fromEmail?.email)}
          />
        }
        nameError={errors.fromEmail?.name?.message}
        emailError={errors.fromEmail?.email?.message}
      />
    </Flex>
  );
};

const EditJournalEmailsModal: React.FC<Props> = ({
  journal,
  isOpen,
  onClose
}) => {
  const toast = useToast();
  const updateSettingMutation = useUpdateJournalSettings(journal.id);

  const methods = useForm<JournalEmailsFormValues>({
    // Rows are only judged when the user asks for them to be saved: nothing is
    // reported while a row is still being typed into, and a correction is not
    // re-checked until the next Save either.
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: getJournalEmailsFormValues(journal)
  });

  const { handleSubmit, reset } = methods;

  const handleClose = () => {
    reset(getJournalEmailsFormValues(journal));
    onClose();
  };

  const onSubmit = async (values: JournalEmailsFormValues) => {
    const res = await updateSettingMutation.mutateAsync(
      buildJournalEmailsPayload(values)
    );

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Emails Edited',
        isClosable: true
      });
      onClose();
      return;
    }

    toast({
      variant: 'error',
      title:
        typeof res?.message === 'string'
          ? res?.message
          : 'Something went wrong',
      isClosable: true
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width={{ base: 'calc(100vw - 16px)', md: '65rem' } as any}
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex flexDirection="column" gap={6} padding={{ base: 4, md: 8 }}>
            <Flex fontSize={32}>
              <FontAwesomeIcon icon={faEdit} />
            </Flex>
            <Text textStyle="headingGroup">Edit Emails</Text>

            {/* Only the fields scroll: the title and the actions below stay in
                view as reply-to and recipient rows are added. */}
            <Flex
              flexDirection="column"
              gap={6}
              maxHeight="60vh"
              overflowY="auto"
              paddingRight={2}
            >
              <FromEmailSection />
              <ReplyToSection />
              <AdditionalEmailsSection />
            </Flex>

            <Flex
              justifyContent="flex-end"
              gap={2}
              alignItems="center"
              marginTop={2}
            >
              <Button
                variant="secondary"
                onClick={handleClose}
                isDisabled={updateSettingMutation.isLoading}
              >
                Exit Without Saving
              </Button>
              <Button
                variant="primary"
                type="submit"
                isLoading={updateSettingMutation.isLoading}
              >
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditJournalEmailsModal;
